import { notificationApi } from '@/api/notifications';

const state = {
  notifications: [],
  unreadCount: 0,
  loading: false,
  error: null,
  pagination: {
    page: 1,
    pageSize: 20,
    totalCount: 0,
    totalPages: 0
  }
};

const mutations = {
  SET_NOTIFICATIONS(state, { results, count, page, page_size, total_pages }) {
    state.notifications = results;
    state.pagination = {
      page,
      pageSize: page_size,
      totalCount: count,
      totalPages: total_pages
    };
  },
  
  APPEND_NOTIFICATIONS(state, { results, count, page, page_size, total_pages }) {
    state.notifications = [...state.notifications, ...results];
    state.pagination = {
      page,
      pageSize: page_size,
      totalCount: count,
      totalPages: total_pages
    };
  },
  
  SET_UNREAD_COUNT(state, count) {
    state.unreadCount = count;
  },
  
  MARK_AS_READ(state, notificationId) {
    const notification = state.notifications.find(n => n.id === notificationId);
    if (notification) {
      notification.is_read = true;
      notification.read_at = new Date().toISOString();
    }
    // Decrement unread count
    if (state.unreadCount > 0) {
      state.unreadCount--;
    }
  },
  
  MARK_ALL_AS_READ(state) {
    state.notifications.forEach(n => {
      n.is_read = true;
      n.read_at = new Date().toISOString();
    });
    state.unreadCount = 0;
  },
  
  DELETE_NOTIFICATION(state, notificationId) {
    const index = state.notifications.findIndex(n => n.id === notificationId);
    if (index !== -1) {
      const wasUnread = !state.notifications[index].is_read;
      state.notifications.splice(index, 1);
      if (wasUnread && state.unreadCount > 0) {
        state.unreadCount--;
      }
    }
    state.pagination.totalCount--;
  },
  
  SET_LOADING(state, loading) {
    state.loading = loading;
  },
  
  SET_ERROR(state, error) {
    state.error = error;
  },
  
  CLEAR_ERROR(state) {
    state.error = null;
  }
};

const actions = {
  async fetchNotifications({ commit }, params = {}) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');
    
    try {
      const response = await notificationApi.getNotifications(params);
      commit('SET_NOTIFICATIONS', response.data);
      return response.data;
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to fetch notifications');
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },
  
  async loadMoreNotifications({ commit, state }) {
    const nextPage = state.pagination.page + 1;
    if (nextPage > state.pagination.totalPages) return;
    
    commit('SET_LOADING', true);
    
    try {
      const response = await notificationApi.getNotifications({
        page: nextPage,
        page_size: state.pagination.pageSize
      });
      commit('APPEND_NOTIFICATIONS', response.data);
      return response.data;
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to load more notifications');
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },
  
  async fetchUnreadCount({ commit }) {
    try {
      const response = await notificationApi.getUnreadCount();
      commit('SET_UNREAD_COUNT', response.data.unread_count);
      return response.data.unread_count;
    } catch (error) {
      console.error('Failed to fetch unread count:', error);
      return 0;
    }
  },
  
  async markAsRead({ commit }, notificationId) {
    try {
      const response = await notificationApi.markAsRead(notificationId);
      commit('MARK_AS_READ', notificationId);
      return response.data;
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
      throw error;
    }
  },
  
  async markAllAsRead({ commit }) {
    try {
      await notificationApi.markAllAsRead();
      commit('MARK_ALL_AS_READ');
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
      throw error;
    }
  },
  
  async deleteNotification({ commit }, notificationId) {
    try {
      await notificationApi.deleteNotification(notificationId);
      commit('DELETE_NOTIFICATION', notificationId);
    } catch (error) {
      console.error('Failed to delete notification:', error);
      throw error;
    }
  }
};

const getters = {
  unreadNotifications: state => state.notifications.filter(n => !n.is_read),
  readNotifications: state => state.notifications.filter(n => n.is_read),
  hasUnread: state => state.unreadCount > 0,
  getNotificationById: state => id => state.notifications.find(n => n.id === id)
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
};
